/**
 * Helpers for collapsing the per-subtask clone names produced by
 * multi-objective Composed Task uploads into the single user-visible raw
 * filename.
 *
 * Backend uploads attach a different per-subtask clone to each sibling
 * ``LLMUsecaseInfo`` (e.g. ``my_dataset__t_cefecd`` for Classification and
 * ``my_dataset__t_cefecf`` for NER). The clone name comes from
 * ``make_clone_dataset_name(filename, usecase_id)`` which appends a
 * deterministic ``__t_<6-char usecase suffix>`` token. The Data Overview
 * card aggregates files across siblings; without a collapse helper the
 * user would see N tiles for what is logically the same uploaded file.
 */

/**
 * Regex for the clone-name suffix appended by ``make_clone_dataset_name``.
 *
 * The 6-char tail is the lowercase hex/alpha-numeric tail of the owning
 * usecase id. Anchored to the end of the string so we never strip names
 * that just happen to contain the substring. The ``+`` allows the suffix
 * to repeat for chained clones (``my_raw__t_xxxxxx__t_yyyyyy``) so a
 * single ``replace`` collapses an N-deep clone chain back to the raw.
 */
export const CLONE_NAME_SUFFIX_RE = /(__t_[a-z0-9]{6})+$/i;

/** Unanchored clone suffix — used to scrub names interpolated into sentences. */
export const CLONE_NAME_SUFFIX_IN_TEXT_RE = /(__t_[a-z0-9]{6})+/gi;

/**
 * Strip every trailing ``__t_<6chars>`` clone suffix to recover the
 * original raw (library) dataset name. Handles chained clones (the
 * background rescue path can treat a single-suffix clone as a raw and
 * clone it again, producing ``my_raw__t_xxxxxx__t_yyyyyy`` — both
 * tails must come off so siblings collapse to one tile). Returns the
 * input unchanged for non-strings or names that don't carry the suffix.
 */
export const stripCloneSuffix = (name) =>
    typeof name === 'string' ? name.replace(CLONE_NAME_SUFFIX_RE, '') : name;

/**
 * Strip every ``__t_<6chars>`` clone suffix that appears inside free text
 * (activity-log descriptions, joined name lists). Leaves non-strings
 * unchanged.
 */
export const stripCloneSuffixInText = (text) =>
    typeof text === 'string' ? text.replace(CLONE_NAME_SUFFIX_IN_TEXT_RE, '') : text;

/**
 * True when ``name`` looks like a Composed-Task per-subtask clone
 * (i.e. ``stripCloneSuffix`` would change it).
 */
export const isCloneDatasetName = (name) =>
    typeof name === 'string' && CLONE_NAME_SUFFIX_RE.test(name);

/**
 * Middle segments of task-scoped SDK dataset names. ``_sdk_traces`` holds
 * SpecificAI model inference traces; ``_sdk_logs`` holds external (non-SpecificAI)
 * model interactions logged via ``client.log*``.
 */
export const SDK_TRACE_DATASET_MARKER = '_sdk_traces';
export const SDK_LOG_DATASET_MARKER = '_sdk_logs';

/** ``{usecase}_sdk_(traces|logs)__t_<6chars>`` — clone suffix is trailing, like upload clones. */
export const SDK_TRACE_DATASET_RE = /_sdk_(traces|logs)__t_[a-z0-9]{6}$/i;

export const isSdkTraceDatasetName = (name) =>
    typeof name === 'string' && SDK_TRACE_DATASET_RE.test(name);

/** User-facing label for an SDK dataset tile or removal dialog. */
export const getSdkTraceDisplayName = (name) => {
    if (!isSdkTraceDatasetName(name)) {
        return name;
    }
    const isLog = name.replace(CLONE_NAME_SUFFIX_RE, '').endsWith(
        SDK_LOG_DATASET_MARKER
    );
    const marker = isLog ? SDK_LOG_DATASET_MARKER : SDK_TRACE_DATASET_MARKER;
    const withoutClone = name.replace(CLONE_NAME_SUFFIX_RE, '');
    const usecaseBase = withoutClone.replace(new RegExp(`${marker}$`), '');
    return `${usecaseBase} (SDK ${isLog ? 'logs' : 'traces'})`;
};

/**
 * User-facing dataset / benchmark / file name. Upload clones lose the
 * ``__t_<6chars>`` suffix; SDK datasets use the dedicated traces/logs label.
 */
export const getUserFacingDatasetName = (name) => {
    if (typeof name !== 'string' || !name) return name;
    if (isSdkTraceDatasetName(name)) return getSdkTraceDisplayName(name);
    return stripCloneSuffix(name);
};

/**
 * Data Overview file tile label. Same as ``getUserFacingDatasetName``.
 */
export const getDataOverviewFileDisplayName = getUserFacingDatasetName;

/** Join dataset names for UI copy, stripping clone suffixes. */
export const formatDatasetNameList = (names) =>
    (Array.isArray(names) ? names : [])
        .map((n) => getUserFacingDatasetName(n))
        .filter(Boolean)
        .join(', ');

/**
 * Resolve the dataset/benchmark collection name for a table row mutation.
 *
 * Cloned rows may still store the source task's ``dataset`` field in Mongo while
 * the row lives in this task's clone collection. Only trust ``record.dataset``
 * when it is listed on the active usecase; otherwise use the first attachment.
 */
export const resolveDatasetNameForRecord = (record, usecase, isBenchmark) => {
    const names = isBenchmark
        ? (usecase?.benchmarks || [])
        : (usecase?.datasets || []);
    if (record?.dataset && names.includes(record.dataset)) {
        return record.dataset;
    }
    return names[0] || record?.dataset;
};
